import { Box, Grid, Stack, Typography } from '@mui/material';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InventoryRoundedIcon from '@mui/icons-material/Inventory2Rounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import { ChartCard } from '../chart-card';
import { ReportTable } from '../report-table';
import { num, palette } from '../report-theme';
import { ReportModel } from '../report-data';
import { neutral } from '../../../theme/colors';

const statusMeta: Record<string, { label: string; color: string; Icon: typeof ErrorOutlineRoundedIcon }> = {
	CRITICAL_LOW: { label: 'Critical', color: palette.status.critical, Icon: ErrorOutlineRoundedIcon },
	LOW: { label: 'Low', color: palette.status.warning, Icon: WarningAmberRoundedIcon },
	OVERSTOCK: { label: 'Overstock', color: palette.status.info, Icon: InventoryRoundedIcon },
	HEALTHY: { label: 'Healthy', color: palette.status.positive, Icon: CheckCircleOutlineRoundedIcon },
};

/** Status reads as an icon + word, never colour alone. */
const StatusTag = ({ status }: { status?: string }) => {
	const meta = statusMeta[status ?? 'HEALTHY'] ?? {
		label: status ?? 'Unknown',
		color: palette.status.neutral,
		Icon: InventoryRoundedIcon,
	};
	const { Icon } = meta;
	return (
		<Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: meta.color }}>
			<Icon sx={{ fontSize: 16 }} />
			<Typography variant="body2" sx={{ fontWeight: 600, color: meta.color }}>
				{meta.label}
			</Typography>
		</Stack>
	);
};

const AlertBanner = ({
	tone,
	title,
	detail,
}: {
	tone: 'critical' | 'warning';
	title: string;
	detail: string;
}) => {
	const color = tone === 'critical' ? palette.status.critical : palette.status.warning;
	const Icon = tone === 'critical' ? ErrorOutlineRoundedIcon : WarningAmberRoundedIcon;
	return (
		<Stack
			direction="row"
			spacing={1.5}
			sx={{
				p: 2,
				borderRadius: 2,
				border: `1px solid ${color}33`,
				backgroundColor: `${color}0F`,
			}}
		>
			<Icon sx={{ color, fontSize: 20, mt: '2px' }} />
			<Box>
				<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
					{title}
				</Typography>
				<Typography variant="body2" color="text.secondary">
					{detail}
				</Typography>
			</Box>
		</Stack>
	);
};

export const InventoryTab = ({ report }: { report: ReportModel }) => {
	const rank = (status?: string) =>
		status === 'CRITICAL_LOW' ? 0 : status === 'LOW' ? 1 : status === 'OVERSTOCK' ? 2 : 3;
	const items = [...report.inventoryAnalysis].sort(
		(a, b) => rank(a.status) - rank(b.status) || (a.daysUntilStockout ?? 9999) - (b.daysUntilStockout ?? 9999)
	);

	return (
		<Grid container spacing={3}>
			{(report.lowStockCount > 0 || report.overstockCount > 0) && (
				<Grid item xs={12}>
					<Grid container spacing={2}>
						{report.lowStockCount > 0 && (
							<Grid item xs={12} md={6}>
								<AlertBanner
									tone="critical"
									title={`${num(report.lowStockCount)} product${report.lowStockCount === 1 ? '' : 's'} running low`}
									detail="Reorder before these run out — stock levels are at or below the reorder point."
								/>
							</Grid>
						)}
						{report.overstockCount > 0 && (
							<Grid item xs={12} md={6}>
								<AlertBanner
									tone="warning"
									title={`${num(report.overstockCount)} product${report.overstockCount === 1 ? ' is' : 's are'} overstocked`}
									detail="Capital is sitting on the shelf. Consider a promotion or pausing reorders."
								/>
							</Grid>
						)}
					</Grid>
				</Grid>
			)}

			<Grid item xs={12}>
				<ChartCard
					title="Stock levels"
					subtitle="Counted as of now — unlike every other tab, this is not scoped to the date range"
					isEmpty={items.length === 0}
					emptyMessage="No inventory is being tracked for this location."
				>
					<ReportTable
						columns={[
							{
								title: 'Product',
								render: (row: any) => (
									<Typography variant="body2" sx={{ fontWeight: 500 }}>
										{row.productName}
									</Typography>
								),
							},
							{ title: 'Status', render: (row: any) => <StatusTag status={row.status} /> },
							{ title: 'In stock', align: 'right', render: (row: any) => num(row.currentStock) },
							{
								title: 'Available',
								align: 'right',
								render: (row: any) => (
									<Stack alignItems="flex-end">
										<Typography variant="body2">{num(row.quantityAvailable)}</Typography>
										{(row.quantityReserved ?? 0) > 0 && (
											<Typography variant="caption" color="text.secondary">
												{`${num(row.quantityReserved)} reserved`}
											</Typography>
										)}
									</Stack>
								),
							},
							{
								title: 'Used in period',
								align: 'right',
								render: (row: any) => num(row.usageInPeriod),
							},
							{
								title: 'Reorder at',
								align: 'right',
								render: (row: any) => (row.reorderLevel != null ? num(row.reorderLevel) : '—'),
							},
							{
								title: 'Runs out in',
								align: 'right',
								render: (row: any) =>
									row.daysUntilStockout != null ? (
										<Typography
											variant="body2"
											sx={{
												fontWeight: row.daysUntilStockout <= 7 ? 600 : 400,
												color: row.daysUntilStockout <= 7 ? palette.status.critical : 'text.primary',
											}}
										>
											{`${num(row.daysUntilStockout)} days`}
										</Typography>
									) : (
										<Typography variant="body2" sx={{ color: neutral[400] }} title="No usage recorded in this period, so a run-out date cannot be projected">
											No usage yet
										</Typography>
									),
							},
						]}
						rows={items}
						emptyMessage="No inventory is being tracked for this location."
						maxHeight={520}
					/>
				</ChartCard>
			</Grid>
		</Grid>
	);
};
